export const MAX_AGENT_IMAGE_DIMENSION = 8000;
export const MAX_AGENT_IMAGE_PIXELS = 20_000_000;
export const AGENT_IMAGE_LIMIT_HINT = '图片单边不超过 8000px，总像素不超过 2000 万';

export function agentImageDimensionError(width: number, height: number): string | null {
  if (!Number.isSafeInteger(width) || !Number.isSafeInteger(height) || width < 1 || height < 1)
    return '图片尺寸不合法';
  return width > MAX_AGENT_IMAGE_DIMENSION ||
    height > MAX_AGENT_IMAGE_DIMENSION ||
    width * height > MAX_AGENT_IMAGE_PIXELS
    ? AGENT_IMAGE_LIMIT_HINT
    : null;
}

/** 只读取文件头，在完整解码前拒绝超大像素图；可解码性仍须由各端解码器验证。 */
export function inspectAgentAttachmentImage(bytes: Uint8Array): {
  contentType: string;
  width: number;
  height: number;
} | null {
  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  const matches = (offset: number, signature: number[]) =>
    signature.every((value, index) => bytes[offset + index] === value);
  const result = (contentType: string, width: number, height: number) => {
    const error = agentImageDimensionError(width, height);
    if (error) throw new Error(error);
    return { contentType, width, height };
  };
  const invalid = () => {
    throw new Error('图片文件损坏或格式不合法');
  };

  if (matches(0, [0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a])) {
    if (bytes.length < 33 || view.getUint32(8) !== 13 || !matches(12, [0x49, 0x48, 0x44, 0x52]))
      return invalid();
    return result('image/png', view.getUint32(16), view.getUint32(20));
  }
  if (matches(0, [0xff, 0xd8])) {
    let offset = 2;
    while (offset < bytes.length) {
      if (bytes[offset++] !== 0xff) return invalid();
      while (bytes[offset] === 0xff) offset++;
      const marker = bytes[offset++];
      if (marker === undefined || marker === 0xda || marker === 0xd9) return invalid();
      if (marker === 0x01 || (marker >= 0xd0 && marker <= 0xd7)) continue;
      if (offset + 2 > bytes.length) return invalid();
      const length = view.getUint16(offset);
      if (length < 2 || offset + length > bytes.length) return invalid();
      if (marker >= 0xc0 && marker <= 0xcf && ![0xc4, 0xc8, 0xcc].includes(marker)) {
        if (length < 8) return invalid();
        return result('image/jpeg', view.getUint16(offset + 5), view.getUint16(offset + 3));
      }
      offset += length;
    }
    return invalid();
  }
  if (matches(0, [0x52, 0x49, 0x46, 0x46]) && matches(8, [0x57, 0x45, 0x42, 0x50])) {
    if (bytes.length < 20 || view.getUint32(4, true) + 8 !== bytes.length) return invalid();
    const uint24 = (offset: number) =>
      bytes[offset]! | (bytes[offset + 1]! << 8) | (bytes[offset + 2]! << 16);
    for (let offset = 12; offset + 8 <= bytes.length;) {
      const size = view.getUint32(offset + 4, true);
      const data = offset + 8;
      if (data + size > bytes.length) return invalid();
      if (matches(offset, [0x56, 0x50, 0x38, 0x58])) {
        if (size !== 10) return invalid();
        return result('image/webp', uint24(data + 4) + 1, uint24(data + 7) + 1);
      }
      if (matches(offset, [0x56, 0x50, 0x38, 0x4c])) {
        if (size < 5 || bytes[data] !== 0x2f) return invalid();
        const dimensions = view.getUint32(data + 1, true);
        return result('image/webp', (dimensions & 0x3fff) + 1, ((dimensions >>> 14) & 0x3fff) + 1);
      }
      if (matches(offset, [0x56, 0x50, 0x38, 0x20])) {
        if (size < 10 || bytes[data]! & 1 || !matches(data + 3, [0x9d, 0x01, 0x2a]))
          return invalid();
        return result(
          'image/webp',
          view.getUint16(data + 6, true) & 0x3fff,
          view.getUint16(data + 8, true) & 0x3fff,
        );
      }
      offset = data + size + (size % 2);
    }
    return invalid();
  }
  return null;
}
